import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link2, Copy, Mail, Trash2, ShieldCheck, Eye, Sparkles, ChevronRight } from "lucide-react";
import { isoDate, weekStartOf, addDays, formatWeekRange } from "@/lib/time";

const FIELD_DEFS = [
  { id: "hours", label: "Hours by subject", note: "bar chart of minutes vs target" },
  { id: "blocks", label: "Blocks completed", note: "just the count" },
  { id: "streak", label: "Streak days", note: "consecutive days with a completed block" },
  { id: "mood_avg", label: "Average mood", note: "as an emoji only, never a raw number" },
  { id: "epq_milestones", label: "EPQ milestone progress", note: "proposal → research → draft → presentation" },
  { id: "wins", label: "Top wins this week", note: "you pick up to 3 — OFF by default", opt_in: true },
  { id: "note", label: "A short note", note: "up to 300 chars, from you to them" },
];

const MOOD_TO_EMOJI = (m) => (m == null ? "—" : m < 1.5 ? "😔" : m < 2.5 ? "😐" : m < 3.5 ? "🙂" : m < 4.5 ? "😊" : "🌟");

export default function ShareCompose() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => weekStartOf(new Date()));
  const [autoStats, setAutoStats] = useState(null);
  const [thisWeekWins, setThisWeekWins] = useState([]);
  const [enabled, setEnabled] = useState({
    hours: true, blocks: true, streak: true, mood_avg: true, epq_milestones: true, note: true, wins: false,
  });
  const [selectedWinDates, setSelectedWinDates] = useState([]);
  const [personalNote, setPersonalNote] = useState("");
  const [shares, setShares] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const ws = useMemo(() => isoDate(weekStart), [weekStart]);

  useEffect(() => {
    (async () => {
      try {
        const [ref, ci, shr] = await Promise.all([
          api.get(`/weekly-reflections/${ws}`),
          api.get(`/checkins?since=${ws}`),
          api.get("/shares"),
        ]);
        setAutoStats(ref.data.auto_stats);
        const end = isoDate(addDays(weekStart, 6));
        const wins = (ci.data.items || []).filter(
          (c) => c.date >= ws && c.date <= end && (c.win_text || "").trim().length > 0
        );
        setThisWeekWins(wins);
        setShares(shr.data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [ws]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleField = (id) => setEnabled((e) => ({ ...e, [id]: !e[id] }));
  const toggleWin = (dateStr) => setSelectedWinDates((arr) => {
    if (arr.includes(dateStr)) return arr.filter((x) => x !== dateStr);
    if (arr.length >= 3) return arr;
    return [...arr, dateStr];
  });

  const create = async () => {
    setCreating(true);
    try {
      const allowed = Object.keys(enabled).filter((k) => enabled[k]);
      const { data } = await api.post("/shares", {
        allowed_fields: allowed,
        selected_win_dates: enabled.wins ? selectedWinDates : [],
        personal_note: enabled.note ? personalNote : "",
        week_start_date: ws,
      });
      const publicUrl = window.location.origin + data.url;
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Link created & copied.");
      } catch {
        toast.success("Link created.");
      }
      setShares((arr) => [data, ...arr]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("This link will stop working. Sure?")) return;
    try {
      await api.delete(`/shares/${id}`);
      setShares((arr) => arr.map((s) => (s.id === id ? { ...s, is_active: false, revoked_at: new Date().toISOString() } : s)));
      toast.success("Revoked.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async (share) => {
    const url = window.location.origin + `/s/${share.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied.");
    } catch {
      toast.error("Couldn't copy — long-press the link.");
    }
  };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />

      <div className="pt-2 mb-6 fade-up">
        <span className="eyebrow">for someone who's rooting for you</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>Share your week</h1>
        <p className="text-muted mt-1 max-w-xl">You control every field. Nothing private ever leaves your side.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: PREVIEW */}
        <section className="lg:col-span-6" data-testid="share-preview">
          <div className="card-soft p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Eye size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
              <span className="eyebrow">what they'll see</span>
            </div>
            <PreviewMock
              displayName={(user?.email || "you").split("@")[0]}
              weekRange={formatWeekRange(weekStart)}
              enabled={enabled}
              stats={autoStats}
              personalNote={personalNote}
              selectedWinDates={selectedWinDates}
              wins={thisWeekWins}
            />
          </div>

          <div className="card-soft-2 p-4 mt-4 flex items-start gap-3" data-testid="share-privacy-callout">
            <ShieldCheck size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" style={{ color: "var(--primary)" }} />
            <div className="text-sm" style={{ color: "var(--text)" }}>
              <div className="font-medium mb-1">Private, always:</div>
              <div className="text-muted">Your private notes, weekly reflection answers, raw mood scores, and any past week's data are <em>never</em> shared — no toggle for those.</div>
            </div>
          </div>
        </section>

        {/* RIGHT: CONTROLS */}
        <section className="lg:col-span-6 space-y-4">
          <div className="card-soft p-5 fade-up" data-testid="share-fields">
            <span className="eyebrow">what to include</span>
            <div className="mt-3 space-y-2">
              {FIELD_DEFS.map((f) => (
                <label
                  key={f.id}
                  className="flex items-start gap-3 rounded-2xl px-3 py-2 cursor-pointer transition-colors"
                  style={{
                    background: enabled[f.id] ? "color-mix(in oklab, var(--primary) 12%, var(--surface))" : "var(--surface-2)",
                    border: `1px solid ${enabled[f.id] ? "var(--primary)" : "var(--border)"}`,
                  }}
                  data-testid={`share-toggle-${f.id}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 accent-[var(--primary)]"
                    checked={enabled[f.id]}
                    onChange={() => toggleField(f.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--text)" }}>
                      {f.label}
                      {f.opt_in && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--surface)", color: "var(--accent)", border: "1px solid var(--border)" }}>opt-in</span>}
                    </div>
                    <div className="text-xs text-muted mt-0.5">{f.note}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {enabled.wins && (
            <div className="card-soft p-5 fade-up" data-testid="share-wins-picker">
              <span className="eyebrow">pick up to 3 wins</span>
              <div className="mt-3 space-y-2">
                {thisWeekWins.length === 0 && (
                  <div className="text-sm text-muted italic">No wins written this week yet.</div>
                )}
                {thisWeekWins.map((w) => (
                  <label
                    key={w.date}
                    className="flex items-start gap-3 rounded-2xl px-3 py-2 cursor-pointer"
                    style={{
                      background: selectedWinDates.includes(w.date) ? "color-mix(in oklab, var(--primary) 10%, var(--surface))" : "var(--surface-2)",
                      border: `1px solid ${selectedWinDates.includes(w.date) ? "var(--primary)" : "var(--border)"}`,
                    }}
                    data-testid={`share-win-${w.date}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 accent-[var(--primary)]"
                      checked={selectedWinDates.includes(w.date)}
                      onChange={() => toggleWin(w.date)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-muted">{w.date}</div>
                      <div className="font-display italic text-base leading-snug" style={{ color: "var(--text)" }}>"{w.win_text}"</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {enabled.note && (
            <div className="card-soft p-5 fade-up" data-testid="share-note-block">
              <span className="eyebrow">a short note</span>
              <textarea
                className="input-soft mt-2"
                placeholder="Something to say alongside the numbers…"
                value={personalNote}
                onChange={(e) => setPersonalNote(e.target.value)}
                maxLength={300}
                rows={3}
                data-testid="share-note-input"
              />
              <div className="text-xs text-muted mt-1 text-right">{personalNote.length}/300</div>
            </div>
          )}

          <button
            onClick={create}
            disabled={creating || Object.values(enabled).every((v) => !v)}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="share-create-btn"
          >
            <Link2 size={16} strokeWidth={1.5} /> {creating ? "Creating…" : "Share this week"}
          </button>
        </section>
      </div>

      {/* History */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
          <span className="eyebrow">your shares</span>
        </div>
        {loading ? <div className="text-muted">Loading…</div> :
          shares.length === 0 ? (
            <div className="card-soft-2 p-6 text-center font-hand text-lg text-muted" data-testid="share-history-empty">nothing shared yet — the button above sends nothing until you tap it</div>
          ) : (
            <ul className="space-y-2" data-testid="share-history">
              {shares.map((s) => (
                <li key={s.id} className="card-soft-2 p-4 flex items-center gap-3 flex-wrap" data-testid={`share-history-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium" style={{ color: "var(--text)" }}>
                      {s.week_start_date} → {s.week_end_date}
                    </div>
                    <div className="text-xs text-muted">
                      opened {s.opened_count} time{s.opened_count === 1 ? "" : "s"} · {s.unique_viewers_count} viewer{s.unique_viewers_count === 1 ? "" : "s"} · {s.is_active ? <span style={{ color: "var(--primary)" }}>active</span> : <span>revoked</span>}
                    </div>
                  </div>
                  {s.is_active && (
                    <>
                      <button onClick={() => copyLink(s)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`share-copy-${s.id}`}>
                        <Copy size={12} strokeWidth={1.5} /> Copy link
                      </button>
                      <a href={`mailto:?subject=My%20week%20on%20Ascend&body=${encodeURIComponent(window.location.origin + '/s/' + s.share_token)}`} className="btn-ghost text-xs flex items-center gap-1" data-testid={`share-email-${s.id}`}>
                        <Mail size={12} strokeWidth={1.5} /> Email
                      </a>
                      <button onClick={() => revoke(s.id)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`share-revoke-${s.id}`}>
                        <Trash2 size={12} strokeWidth={1.5} /> Revoke
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
      </div>
    </AppShell>
  );
}

function PreviewMock({ displayName, weekRange, enabled, stats, personalNote, selectedWinDates, wins }) {
  return (
    <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, var(--grad-a), var(--grad-b))", border: "1px solid var(--border)" }}>
      <div className="text-xs uppercase tracking-wider" style={{ color: "var(--accent)" }}>{displayName}'s week</div>
      <div className="font-display text-2xl mt-1" style={{ color: "var(--text)" }}>{weekRange}</div>

      {enabled.note && personalNote && (
        <blockquote className="mt-3 italic font-display text-base leading-snug" style={{ color: "var(--text)" }}>"{personalNote}"</blockquote>
      )}

      <div className="mt-4 grid grid-cols-3 gap-2">
        {enabled.blocks && (
          <MiniStat label="blocks" value={stats?.blocks_completed ?? 0} testid="preview-blocks" />
        )}
        {enabled.streak && (
          <MiniStat label="streak" value={`${stats?.streak_days ?? 0}d`} testid="preview-streak" />
        )}
        {enabled.mood_avg && (
          <MiniStat label="mood" value={MOOD_TO_EMOJI(stats?.avg_mood)} testid="preview-mood" />
        )}
      </div>

      {enabled.hours && stats?.subjects?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-muted mb-1">hours by subject</div>
          <div className="space-y-1.5">
            {stats.subjects.map((s) => {
              const targetMin = (s.target_hours || 0) * 60;
              const pct = targetMin > 0 ? Math.min(100, Math.round((s.minutes / targetMin) * 100)) : 0;
              const hours = (s.minutes / 60).toFixed(2).replace(/\.?0+$/, "");
              return (
                <div key={s.subject_id}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.name}</span>
                    <span className="text-muted">{hours}h{s.target_hours ? ` / ${s.target_hours}h` : ""}</span>
                  </div>
                  <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%`, background: s.color }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {enabled.wins && selectedWinDates.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-muted mb-1">wins</div>
          <ul className="space-y-1">
            {wins.filter((w) => selectedWinDates.includes(w.date)).map((w) => (
              <li key={w.date} className="italic font-display text-sm leading-snug" style={{ color: "var(--text)" }}>"{w.win_text}"</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="rounded-xl px-2 py-2 text-center" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid={testid}>
      <div className="font-display text-xl leading-none" style={{ color: "var(--text)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted mt-1">{label}</div>
    </div>
  );
}
